import { useRef, useEffect, useState } from "react";

interface ProgressArcProps {
  score?: number;
  maxScore?: number;
  currentGame?: number;
  size?: number; // Новый пропс для задания размера
}

export const ProgressArc = ({
  score = 0,
  maxScore = 5000,
  currentGame = 2,
  size = 360, // Размер по умолчанию
}: ProgressArcProps) => {
  const pathRef = useRef<SVGPathElement>(null);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
    }
  }, []);

  // Calculate progress as a percentage of maxScore
  const progress = ((Math.min(score / maxScore, 1) * 100) / 100) * 71.43;
  const progressOffset =
    pathLength * (1 - Math.min(Math.max(progress / 100, 0), 1));

  // Calculate thresholds based on position in the arc
  const leftCircleReached = progress >= 20;
  const topCircleReached = progress >= 50;
  const rightCircleReached = progress >= 80;

  // Set the progress color
  const progressColor = "#FB3F50"; // Red progress line as requested

  // Determine circle colors based on progress
  const leftCircleColor = leftCircleReached ? progressColor : "#191513";
  const topCircleColor = topCircleReached ? progressColor : "#191513";
  const rightCircleColor = rightCircleReached ? progressColor : "#191513";

  // Рассчитываем масштабирование на основе переданного размера
  const scale = size / 360;

  return (
    <svg
      width={size}
      height={size * 1.25} // Высота пропорциональна ширине
      viewBox="0 0 650 650"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ transform: `scale(${scale})` }}
    >
      <defs>
        <filter
          id="filter0_dd_934_5926"
          x="0.5"
          y="0"
          width="557.084"
          height="538"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="-0.5" dy="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.16 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_934_5926"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="0.5" dy="-1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.64 0"
          />
          <feBlend
            mode="normal"
            in2="effect1_dropShadow_934_5926"
            result="effect2_dropShadow_934_5926"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect2_dropShadow_934_5926"
            result="shape"
          />
        </filter>
      </defs>

      <g filter="url(#filter0_dd_934_5926)">
        {/* Background track */}
        <path
          d="M93.1522 460.848C56.7906 424.486 32.028 378.159 21.9958 327.723C11.9637 277.288 17.1125 225.011 36.7913 177.502C56.4701 129.994 89.795 89.3871 132.552 60.8179C175.309 32.2487 225.577 17 277 17C328.423 17 378.691 32.2487 421.448 60.8179C464.205 89.3871 497.53 129.994 517.209 177.502C536.887 225.011 542.036 277.288 532.004 327.723C521.972 378.159 497.209 424.486 460.848 460.848"
          stroke="#191513"
          strokeWidth="8"
          strokeLinecap="round"
        />

        {/* Progress track */}
        <path
          ref={pathRef}
          d="M93.1522 460.848C56.7906 424.486 32.028 378.159 21.9958 327.723C11.9637 277.288 17.1125 225.011 36.7913 177.502C56.4701 129.994 89.795 89.3871 132.552 60.8179C175.309 32.2487 225.577 17 277 17C328.423 17 378.691 32.2487 421.448 60.8179C464.205 89.3871 497.53 129.994 517.209 177.502C536.887 225.011 542.036 277.288 532.004 327.723C521.972 378.159 497.209 424.486 460.848 460.848"
          stroke={progressColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={pathLength}
          strokeDashoffset={progressOffset}
          style={{
            transition: "stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />

        {/* Left circle (2000 points) - текст внутри круга */}
        <circle cx="17" cy="277" r="16" fill={leftCircleColor} />
        <text
          x="60"
          y="277"
          textAnchor="start"
          dominantBaseline="middle"
          fill={leftCircleColor}
          fontFamily="barlow, sans-serif"
          fontSize="24"
          fontWeight="bold"
          style={{ filter: "drop-shadow(0px 0px 2px rgba(255,255,255,0.8))" }}
        >
          2000
        </text>

        {/* Top circle (3500 points) - текст внутри круга */}
        <circle cx="277" cy="17" r="16" fill={topCircleColor} />
        <text
          x="277"
          y="55"
          textAnchor="middle"
          dominantBaseline="hanging"
          fill={topCircleColor}
          fontFamily="barlow, sans-serif"
          fontSize="24"
          fontWeight="bold"
          style={{ filter: "drop-shadow(0px 0px 2px rgba(255,255,255,0.8))" }}
        >
          3500
        </text>

        <g opacity="0.32">
          <path
            d="M493.233 468.074C493.954 468.708 494.337 469.398 494.384 470.144C494.43 470.889 494.161 471.587 493.576 472.237L491.938 474.056C491.866 474.135 491.791 474.139 491.712 474.068L483.626 466.787C483.547 466.716 483.543 466.641 483.615 466.562L485.156 464.849C485.77 464.168 486.442 463.804 487.172 463.758C487.895 463.705 488.605 463.993 489.302 464.621C490.19 465.42 490.5 466.295 490.233 467.246C490.226 467.269 490.227 467.284 490.235 467.291C490.243 467.298 490.254 467.301 490.27 467.3C491.302 466.967 492.29 467.225 493.233 468.074ZM485.048 466.452C485.019 466.484 485.021 466.514 485.052 466.543L487.918 469.123C487.95 469.152 487.98 469.15 488.008 469.118L488.854 468.179C489.24 467.751 489.412 467.304 489.373 466.838C489.333 466.371 489.084 465.931 488.624 465.517C488.148 465.089 487.673 464.876 487.197 464.878C486.713 464.873 486.279 465.085 485.893 465.513L485.048 466.452ZM492.773 471.622C493.151 471.201 493.309 470.748 493.246 470.26C493.174 469.765 492.885 469.289 492.378 468.833C491.863 468.369 491.355 468.127 490.856 468.108C490.356 468.089 489.917 468.289 489.539 468.709L488.65 469.696C488.622 469.728 488.623 469.758 488.655 469.787L491.794 472.613C491.826 472.642 491.856 472.64 491.885 472.608L492.773 471.622Z"
            fill="white"
          />
          <path
            d="M513.148 448.19C512.665 448.854 512.066 449.243 511.351 449.356C510.642 449.461 509.946 449.266 509.265 448.77L504.527 445.328C503.845 444.833 503.445 444.232 503.325 443.525C503.212 442.81 503.397 442.12 503.879 441.456C504.369 440.783 504.971 440.39 505.686 440.276C506.401 440.163 507.099 440.354 507.781 440.849L512.519 444.292C513.192 444.781 513.589 445.386 513.711 446.107C513.824 446.822 513.637 447.517 513.148 448.19ZM512.345 447.607C512.633 447.21 512.736 446.796 512.653 446.367C512.567 445.922 512.317 445.549 511.903 445.248L507.075 441.74C506.652 441.433 506.22 441.311 505.779 441.373C505.336 441.42 504.97 441.642 504.682 442.039C504.4 442.427 504.305 442.84 504.397 443.276C504.48 443.705 504.733 444.074 505.156 444.381L509.984 447.889C510.398 448.19 510.827 448.317 511.27 448.269C511.705 448.216 512.063 447.995 512.345 447.607Z"
            fill="white"
          />
          <path
            d="M521.207 414.899C521.115 414.846 521.095 414.773 521.149 414.681L521.549 413.988C521.602 413.895 521.675 413.876 521.767 413.929L531.189 419.369C531.282 419.422 531.301 419.495 531.248 419.588L530.776 420.405C530.733 420.479 530.667 420.509 530.577 420.494L521.853 419.06C521.82 419.053 521.803 419.061 521.802 419.085C521.791 419.104 521.8 419.121 521.827 419.137L528.957 423.235C529.05 423.288 529.069 423.361 529.016 423.454L528.608 424.16C528.555 424.253 528.482 424.272 528.389 424.219L518.967 418.779C518.875 418.726 518.855 418.653 518.909 418.56L519.373 417.757C519.415 417.683 519.482 417.653 519.571 417.668L528.317 419.096C528.35 419.103 528.372 419.097 528.383 419.079C528.384 419.055 528.371 419.035 528.343 419.019L521.207 414.899Z"
            fill="white"
          />
          <path
            d="M543.996 394.065C543.662 394.815 543.135 395.304 542.415 395.532C541.694 395.76 540.91 395.685 540.063 395.308L532.681 392.022C532.584 391.978 532.557 391.908 532.6 391.81L532.932 391.065C532.975 390.967 533.046 390.94 533.143 390.984L540.642 394.322C541.178 394.561 541.67 394.622 542.118 394.507C542.571 394.382 542.895 394.1 543.09 393.661C543.29 393.213 543.285 392.779 543.075 392.358C542.865 391.938 542.492 391.608 541.956 391.37L534.458 388.031C534.36 387.988 534.333 387.917 534.377 387.82L534.709 387.075C534.752 386.977 534.822 386.95 534.92 386.993L542.301 390.28C543.149 390.657 543.727 391.195 544.035 391.893C544.348 392.581 544.335 393.305 543.996 394.065Z"
            fill="white"
          />
          <path
            d="M554.887 364.842C554.643 365.592 554.197 366.12 553.548 366.425C552.9 366.73 552.175 366.753 551.373 366.492L551.023 366.379C550.922 366.346 550.887 366.279 550.92 366.177L551.163 365.431C551.196 365.33 551.263 365.296 551.364 365.329L551.669 365.428C552.206 365.602 552.687 365.607 553.111 365.442C553.524 365.274 553.802 364.971 553.944 364.535C554.082 364.109 554.04 363.703 553.818 363.317C553.596 362.93 553.226 362.653 552.709 362.485C552.364 362.373 552.047 362.348 551.757 362.411C551.461 362.461 551.185 362.567 550.93 362.731C550.668 362.882 550.262 363.148 549.711 363.53C549.171 363.915 548.721 364.212 548.362 364.421C547.993 364.626 547.589 364.764 547.151 364.835C546.716 364.895 546.245 364.843 545.738 364.678C544.947 364.421 544.397 363.99 544.089 363.385C543.77 362.777 543.73 362.108 543.967 361.378C544.218 360.607 544.671 360.075 545.326 359.784C545.975 359.478 546.715 359.461 547.547 359.731L547.928 359.855C548.029 359.888 548.063 359.955 548.03 360.056L547.783 360.817C547.75 360.919 547.683 360.953 547.582 360.92L547.186 360.791C546.648 360.617 546.173 360.614 545.759 360.782C545.338 360.937 545.056 361.238 544.911 361.684C544.779 362.09 544.817 362.472 545.026 362.832C545.224 363.188 545.587 363.452 546.114 363.623C546.429 363.725 546.726 363.76 547.006 363.728C547.279 363.682 547.56 363.577 547.848 363.412C548.137 363.248 548.528 362.994 549.022 362.65C549.666 362.186 550.178 361.853 550.557 361.651C550.93 361.436 551.31 361.302 551.698 361.248C552.075 361.191 552.497 361.239 552.964 361.39C553.796 361.661 554.388 362.116 554.74 362.758C555.082 363.396 555.131 364.091 554.887 364.842Z"
            fill="white"
          />
        </g>

        {/* Right circle (5000 points) - текст внутри круга */}
        <circle cx="537" cy="277" r="16" fill={rightCircleColor} />
        <text
          x="490"
          y="277"
          textAnchor="end"
          dominantBaseline="middle"
          fill={rightCircleColor}
          fontFamily="barlow, sans-serif"
          fontSize="24"
          fontWeight="bold"
          style={{ filter: "drop-shadow(0px 0px 2px rgba(255,255,255,0.8))" }}
        >
          5000
        </text>

        {/* Score display - в одной строке, внизу */}
        <text
          x="277"
          y="500"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Barlow, Arial, sans-serif"
        >
          <tspan fill={progressColor} fontSize="36" fontWeight="bold">
            {score}
          </tspan>
          <tspan fill="#FFFFFF" fontSize="30" fontWeight="500" dx="8">
            / {maxScore}
          </tspan>
        </text>

        {/* Games text - по центру */}
        <text
          x="277"
          y="230"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Barlow, Arial, sans-serif"
          fontSize="24"
          fontWeight="400"
          fill="#d9d9d9"
        >
          GAMES
        </text>

        {/* Game counter - крупным шрифтом по центру */}
        <text
          x="277"
          y="300"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Barlow, Arial, sans-serif"
          fontSize="80"
          fontWeight="700"
          fill="#d9d9d9"
        >
          {currentGame}
          <tspan dx="12" fontSize="80">
            |
          </tspan>
          <tspan dx="12" fontSize="80">
            5
          </tspan>
        </text>
      </g>
    </svg>
  );
};

export default ProgressArc;
